import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
// ── the theme stack, in cascade order. Do not reorder (TEM-1239) ───────────────
// 1. The ecosystem PRIMITIVE layer. cinematic-light.css reads 12
//    --color-cinematic-light-* names that exist nowhere else, so without this the
//    light theme resolves to nothing and the page renders UNSTYLED, not dark.
//    It must sit BEFORE cinematic.css: its unscoped :root re-declares
//    --font-family-{display,body,mono} as the RETIRED Syne / Space Grotesk pair
//    and --shadow-{sm,md,lg,xl} as the pre-v3 elevations, both of which
//    cinematic.css's bridge retargets. Imported after, it would undo them.
//    This is also the first real import of @tempest/design-tokens in Radio —
//    the package has been a declared-but-unused dependency since it was added.
import '@tempest/design-tokens/css/design-tokens.css';
// 2. Radio's own DARK world — the boot state and the hero. Imported after
//    index.css / themes.css / App.css so it wins the cascade at equal specificity.
import './cinematic.css';
// 3. The shared LIGHT layer. Completely inert until something stamps
//    data-theme="cinematic-light" on <html> (utils/theme.ts), so dark renders
//    byte-identically with this line present. Last, so its
//    :root[data-theme="cinematic-light"] block outranks everything above it.
import '@tempest/design-tokens/css/cinematic-light.css';

// Pages
import Home from './pages/Home';
import Streams from './pages/Streams';
import StreamDetail from './pages/StreamDetail';
import LiveStreams from './pages/LiveStreams';
import LiveStreamDetail from './pages/LiveStreamDetail';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import CompleteProfile from './pages/CompleteProfile';
import AdminStreams from './pages/admin/Streams';
import AdminLiveStreams from './pages/admin/LiveStreams';
import AdminAnalytics from './pages/admin/Analytics';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import FollowersList from './pages/FollowersList';
import FollowingList from './pages/FollowingList';
import Favorites from './pages/Favorites';
import ListenHistory from './pages/ListenHistory';
import ActivityFeed from './pages/ActivityFeed';
import CreatorAnalytics from './pages/CreatorAnalytics';
import Settings from './pages/Settings';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Context
// NOTE: still no theme provider and no toggle. The theme is stamped once on
// <html> at boot by utils/theme.ts (index.tsx), which is all CSS needs; React
// never re-renders on it. Dark is the default. TEM-1195's removal of Radio's own
// divergent `light` vocabulary stands — see src/cinematic.css's header.
import { OfflineProvider } from './context/OfflineContext';
import { ErrorToastProvider } from './contexts/ErrorToastContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { BroadcastProvider } from './contexts/BroadcastContext';
import { AuthPromptProvider } from './contexts/AuthPromptContext';
import { NotificationProvider } from './contexts/NotificationContext';

/**
 * `/stream/:id` (singular) is not a page: it is the path Record's Share mode
 * used to copy (TEM-1631), which fell through to the catch-all and opened Home.
 * Links already shared keep working by landing on `streams/:id`, the set's real
 * route — REPLACE, so Back does not bounce through the dead URL. The id is
 * re-encoded as one segment; the query string and hash ride along.
 */
function LegacyStreamRedirect() {
  const { id = '' } = useParams<{ id: string }>();
  const { search, hash } = useLocation();
  return (
    <Navigate to={{ pathname: `/streams/${encodeURIComponent(id)}`, search, hash }} replace />
  );
}

function App() {
  return (
    <OfflineProvider>
        <ErrorToastProvider>
        <AudioPlayerProvider>
          <BroadcastProvider>
            <Authenticator.Provider>
              <NotificationProvider>
              <BrowserRouter>
                <AuthPromptProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="streams" element={<Streams />} />
            <Route path="streams/:id" element={<StreamDetail />} />
            <Route path="stream/:id" element={<LegacyStreamRedirect />} />
            <Route path="live" element={<LiveStreams />} />
            <Route path="live/:id" element={<LiveStreamDetail />} />
            <Route path="users/:username" element={<PublicProfile />} />
            <Route path="users/:username/followers" element={<FollowersList />} />
            <Route path="users/:username/following" element={<FollowingList />} />
            <Route path="about" element={<About />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="terms" element={<Terms />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route path="complete-profile" element={<CompleteProfile />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="favorites" element={<Favorites />} />
              <Route path="history" element={<ListenHistory />} />
              <Route path="activity" element={<ActivityFeed />} />
              <Route path="analytics" element={<CreatorAnalytics />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Stream Management Routes - Available to all authenticated users */}
            <Route element={<ProtectedRoute />}>
              <Route path="manage-streams" element={<AdminStreams />} />
              <Route path="go-live" element={<AdminLiveStreams />} />
            </Route>

            {/* The standalone upload page is retired (TEM-1662, Rich 2026-10-06):
                nothing linked to it, and Manage Streams' New Stream form is the
                one way to publish a set. A kept URL lands there (REPLACE, so
                Back does not return to the dead path), not on the home page. */}
            <Route path="upload" element={<Navigate to="/manage-streams" replace />} />
            <Route path="admin/upload" element={<Navigate to="/manage-streams" replace />} />

            {/* Admin Routes - Only for admin users */}
            <Route element={<ProtectedRoute requireAdmin={true} />}>
              <Route path="admin/streams" element={<AdminStreams />} />
              <Route path="admin/live" element={<AdminLiveStreams />} />
              <Route path="admin/analytics" element={<AdminAnalytics />} />
            </Route>
          </Route>

          {/* Auth Route */}
          <Route
            path="/login"
            element={
              <Authenticator
                formFields={{
                  signUp: {
                    preferred_username: {
                      label: 'Username',
                      placeholder: 'Enter your username',
                      isRequired: true,
                      order: 1,
                    },
                    email: {
                      label: 'Email',
                      placeholder: 'Enter your email',
                      isRequired: true,
                      order: 2,
                    },
                    password: {
                      label: 'Password',
                      placeholder: 'Enter your password',
                      isRequired: true,
                      order: 3,
                    },
                    confirm_password: {
                      label: 'Confirm Password',
                      placeholder: 'Confirm your password',
                      order: 4,
                    },
                  },
                }}
                services={{
                  async validateCustomSignUp(formData) {
                    const username = formData.preferred_username as string;

                    if (!username) {
                      return { preferred_username: 'Username is required' };
                    }

                    // Validate username format
                    if (username.length < 3 || username.length > 20) {
                      return { preferred_username: 'Username must be between 3 and 20 characters' };
                    }

                    const usernameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9_])*[a-zA-Z0-9]$/;
                    if (!usernameRegex.test(username)) {
                      return { preferred_username: 'Username must contain only letters, numbers, and underscores, and cannot start or end with an underscore' };
                    }

                    if (username.includes('__')) {
                      return { preferred_username: 'Username cannot contain consecutive underscores' };
                    }

                    const reserved = ['admin', 'tempest', 'support', 'help', 'api', 'www', 'mail', 'ftp', 'root', 'system'];
                    if (reserved.includes(username.toLowerCase())) {
                      return { preferred_username: 'This username is reserved' };
                    }

                    // Return empty object for no errors
                    return {} as any;
                  },
                }}
              >
                {() => <Navigate to="/complete-profile" replace />}
              </Authenticator>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
                </AuthPromptProvider>
              </BrowserRouter>
              </NotificationProvider>
            </Authenticator.Provider>
          </BroadcastProvider>
        </AudioPlayerProvider>
        </ErrorToastProvider>
    </OfflineProvider>
  );
}

export default App;
